import { useEffect, useState } from 'react'
import type { Area } from 'react-easy-crop'
import { createImage, getCroppedImageBlob } from '../utils/imageCrop'

const INITIAL_CROP = { x: 0, y: 0 }
const INITIAL_ZOOM = 1

type ImageCropErrorPhase = 'load' | 'crop'

interface UseImageCropOptions {
  initialAspect?: number
  onError?: (error: Error, phase: ImageCropErrorPhase) => void
  preprocessFile?: (file: File) => Promise<File>
}

interface UseImageCropResult {
  previewUrl: string | null
  crop: { x: number; y: number }
  setCrop: (crop: { x: number; y: number }) => void
  zoom: number
  setZoom: (zoom: number) => void
  imageAspect: number
  onCropComplete: (_: Area, croppedPixels: Area) => void
  handleFileDrop: (acceptedFiles: File[]) => Promise<boolean>
  handleCropSave: () => Promise<File | null>
  reset: () => void
  croppedFile: File | null
  selectedFile: File | null
}

export default function useImageCrop({
  initialAspect = 4 / 3,
  onError,
  preprocessFile
}: UseImageCropOptions = {}): UseImageCropResult {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [croppedFile, setCroppedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState(INITIAL_CROP)
  const [zoom, setZoom] = useState(INITIAL_ZOOM)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [imageAspect, setImageAspect] = useState(initialAspect)

  const reset = () => {
    setSelectedFile(null)
    setCroppedFile(null)
    setCrop(INITIAL_CROP)
    setZoom(INITIAL_ZOOM)
    setCroppedAreaPixels(null)
    setImageAspect(initialAspect)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(null)
  }

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const handleFileDrop = async (acceptedFiles: File[]): Promise<boolean> => {
    const droppedFile = acceptedFiles?.[0]
    if (!droppedFile || !droppedFile.type.startsWith('image/')) {
      return false
    }

    let nextFile = droppedFile
    if (preprocessFile) {
      try {
        nextFile = await preprocessFile(droppedFile)
      } catch (_error) {
        nextFile = droppedFile
      }
    }

    setSelectedFile(nextFile)
    setCroppedFile(null)
    setCrop(INITIAL_CROP)
    setZoom(INITIAL_ZOOM)
    setCroppedAreaPixels(null)

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }

    const nextPreviewUrl = URL.createObjectURL(nextFile)
    setPreviewUrl(nextPreviewUrl)

    try {
      const image = await createImage(nextPreviewUrl)
      if (image.width > 0 && image.height > 0) {
        setImageAspect(image.width / image.height)
      }
      return true
    } catch (error) {
      URL.revokeObjectURL(nextPreviewUrl)
      setPreviewUrl(null)
      onError?.((error as Error) ?? new Error('Failed to load image'), 'load')
      return false
    }
  }

  const onCropComplete = (_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }

  const handleCropSave = async (): Promise<File | null> => {
    if (!selectedFile || !previewUrl || !croppedAreaPixels) {
      return null
    }

    try {
      const blob = await getCroppedImageBlob(previewUrl, croppedAreaPixels, selectedFile.type)
      const file = new File([blob], selectedFile.name, {
        type: selectedFile.type,
        lastModified: Date.now()
      })
      setCroppedFile(file)
      return file
    } catch (error) {
      onError?.((error as Error) ?? new Error('Failed to crop image'), 'crop')
      return null
    }
  }

  return {
    previewUrl,
    crop,
    setCrop,
    zoom,
    setZoom,
    imageAspect,
    onCropComplete,
    handleFileDrop,
    handleCropSave,
    reset,
    croppedFile,
    selectedFile
  }
}
